import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Brain, ArrowLeft, Phone, ExternalLink, Heart, AlertTriangle,
  MessageCircle, Baby, Smile, Frown, Meh, Sun, Moon, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const moodOptions = [
  { value: 'calm', label: 'Calm', icon: Smile, color: 'text-emerald-500' },
  { value: 'hopeful', label: 'Hopeful', icon: Sun, color: 'text-amber-500' },
  { value: 'neutral', label: 'Neutral', icon: Meh, color: 'text-stone-500' },
  { value: 'stressed', label: 'Stressed', icon: Activity, color: 'text-orange-500' },
  { value: 'anxious', label: 'Anxious', icon: Moon, color: 'text-purple-500' },
  { value: 'overwhelmed', label: 'Overwhelmed', icon: Frown, color: 'text-red-500' }
];

export default function WellnessSanctuary() {
  const { user } = useAuth();
  
  const [resources, setResources] = useState(null);
  const [activeTab, setActiveTab] = useState('resources');
  const [checkin, setCheckin] = useState({
    mood: '',
    energy_level: 3,
    stress_level: 3,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const res = await fetch(`${API}/api/foundation/wellness/resources`);
      if (res.ok) {
        setResources(await res.json());
      }
    } catch (err) {
      console.error('Error fetching resources:', err);
    }
  };

  const submitCheckin = async () => {
    if (!checkin.mood) {
      toast.error('Please select your mood');
      return;
    }
    if (!user) {
      toast.error('Please log in to save check-ins');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/wellness/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(checkin)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        setCheckin({ mood: '', energy_level: 3, stress_level: 3, notes: '' });
        
        // Show resources if needed
        if (data.show_resources) {
          setActiveTab('crisis');
          toast.info('We have some resources that might help you.');
        }
      }
    } catch (err) {
      toast.error('Failed to save check-in');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-emerald-50/30" data-testid="wellness-sanctuary">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/foundation" className="inline-flex items-center text-emerald-200 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Foundation
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Brain className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Wellness Sanctuary</h1>
          </div>
          <p className="text-emerald-100">A safe space for mental health support and self-care.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Crisis Alert */}
        <Card className="mb-8 bg-red-50 border-red-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <AlertTriangle className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-red-800">
                  If you are in immediate danger, please call 911.
                </p>
                <p className="text-sm text-red-600">
                  For crisis support: Call or text <strong>988</strong> (Suicide & Crisis Lifeline)
                </p>
              </div>
              <a href="tel:988">
                <Button className="bg-red-600 hover:bg-red-700">
                  <Phone className="h-4 w-4 mr-2" /> Call 988
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button 
            variant={activeTab === 'resources' ? 'default' : 'outline'}
            onClick={() => setActiveTab('resources')}
            className={activeTab === 'resources' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <Heart className="h-4 w-4 mr-2" /> Support Resources
          </Button>
          <Button 
            variant={activeTab === 'checkin' ? 'default' : 'outline'}
            onClick={() => setActiveTab('checkin')}
            className={activeTab === 'checkin' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <Activity className="h-4 w-4 mr-2" /> Wellness Check-In
          </Button>
          <Button 
            variant={activeTab === 'crisis' ? 'default' : 'outline'}
            onClick={() => setActiveTab('crisis')}
            className={activeTab === 'crisis' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <Phone className="h-4 w-4 mr-2" /> Crisis Lines
          </Button>
          <Button 
            variant={activeTab === 'parenting' ? 'default' : 'outline'}
            onClick={() => setActiveTab('parenting')}
            className={activeTab === 'parenting' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            <Baby className="h-4 w-4 mr-2" /> Parenting Support
          </Button>
        </div>

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="md:col-span-2 lg:col-span-3 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
              <CardContent className="py-8 text-center">
                <Brain className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-stone-800 mb-2">You Are Not Alone</h2>
                <p className="text-stone-600 max-w-2xl mx-auto">
                  Mental wellness is a journey, not a destination. Whether you're feeling stressed, anxious, 
                  or just need someone to talk to, we have resources to support you. Remember: seeking help is a sign of strength.
                </p>
              </CardContent>
            </Card>

            {/* Tele-Therapy Partners */}
            {resources?.tele_therapy_partners?.map((partner, idx) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <Badge variant="outline" className="w-fit mb-2">{partner.type}</Badge>
                    <CardTitle className="text-lg">{partner.name}</CardTitle>
                    <CardDescription>{partner.cost}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <a href={partner.url} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                        <ExternalLink className="h-4 w-4 mr-2" /> Visit Website
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            ))}

            {/* Note about tele-therapy */}
            <Card className="md:col-span-2 lg:col-span-3 bg-blue-50 border-blue-200">
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <MessageCircle className="h-6 w-6 text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-medium text-blue-800 mb-1">About These Resources</h3>
                    <p className="text-sm text-blue-700">
                      These are external tele-therapy partners and resources. DowUrk does not provide direct therapy services. 
                      Please contact these organizations directly for appointments and pricing. Some offer sliding scale fees 
                      based on income.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Check-In Tab */}
        {activeTab === 'checkin' && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>How Are You Feeling Today?</CardTitle>
                <CardDescription>
                  A quick check-in helps you track your wellness journey. Your responses are private.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mood Selection */}
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-3 block">Current Mood</label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {moodOptions.map((mood) => (
                      <button
                        key={mood.value}
                        onClick={() => setCheckin({...checkin, mood: mood.value})}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          checkin.mood === mood.value 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <mood.icon className={`h-6 w-6 mx-auto ${mood.color}`} />
                        <span className="text-xs text-stone-600 block mt-1">{mood.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Energy Level */}
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-2 block">
                    Energy Level: {checkin.energy_level}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={checkin.energy_level}
                    onChange={(e) => setCheckin({...checkin, energy_level: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-stone-400">
                    <span>Low</span>
                    <span>High</span>
                  </div>
                </div>

                {/* Stress Level */}
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-2 block">
                    Stress Level: {checkin.stress_level}/5
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={checkin.stress_level}
                    onChange={(e) => setCheckin({...checkin, stress_level: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-stone-400">
                    <span>Relaxed</span>
                    <span>Very Stressed</span>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-2 block">
                    Anything on your mind? (optional)
                  </label>
                  <Textarea
                    placeholder="Write anything you'd like to process..."
                    value={checkin.notes}
                    onChange={(e) => setCheckin({...checkin, notes: e.target.value})}
                    className="min-h-[100px]"
                  />
                </div>

                <Button 
                  onClick={submitCheckin}
                  disabled={submitting || !user}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {submitting ? "Saving..." : user ? "Save Check-In" : "Log in to Save"}
                </Button>

                {!user && (
                  <p className="text-center text-sm text-stone-500">
                    <Link to="/login" className="text-emerald-600 hover:underline">Log in</Link> to save your wellness check-ins
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Crisis Lines Tab */}
        {activeTab === 'crisis' && (
          <div className="max-w-2xl mx-auto space-y-4">
            {resources?.crisis_resources?.map((resource, idx) => (
              <motion.div
                key={resource.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-stone-800">{resource.name}</h3>
                        <p className="text-sm text-stone-600">{resource.description}</p>
                        <p className="text-lg font-mono text-red-600 mt-1">{resource.contact}</p>
                      </div>
                      {resource.type === 'phone' && (
                        <a href={`tel:${resource.contact}`}>
                          <Button className="bg-red-600 hover:bg-red-700">
                            <Phone className="h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Parenting Tab */}
        {activeTab === 'parenting' && (
          <div className="max-w-3xl mx-auto">
            <Card className="mb-6 bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
              <CardContent className="py-8 text-center">
                <Baby className="h-12 w-12 text-pink-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-stone-800 mb-2">Parenting Support</h2>
                <p className="text-stone-600">
                  Parenting is one of the hardest and most important jobs. 
                  You don't have to do it alone.
                </p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-3 gap-4">
              {resources?.parenting_resources?.map((resource, idx) => (
                <motion.div
                  key={resource.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="h-full">
                    <CardContent className="py-6">
                      <h3 className="font-medium text-stone-800 mb-2">{resource.title}</h3>
                      <a href={resource.url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="w-full">
                          <ExternalLink className="h-4 w-4 mr-2" /> Visit
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Grounded Parent Badge CTA */}
            <Card className="mt-6 bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
              <CardContent className="py-6">
                <div className="flex items-center gap-4">
                  <Baby className="h-10 w-10" />
                  <div className="flex-1">
                    <h3 className="font-bold">Grounded Parent Badge</h3>
                    <p className="text-emerald-100 text-sm">
                      Complete the parenting support track to earn this badge.
                    </p>
                  </div>
                  <Link to="/foundation/school">
                    <Button className="bg-white text-emerald-600 hover:bg-emerald-50">
                      View Courses
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
